// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 65.5.0 AC3 — the from→to legality table for a contract status.
 *
 * Until this sprint a status change was an assignment: `updateContractStatus`
 * checked that the target was a member of the union and wrote it. There was no
 * table, so `terminated → active`, `expired → draft` and `active → draft` were
 * all accepted, and nothing recorded who decided or why.
 *
 * The table is derived from what each status already means in this module, not
 * from any external instrument (R-46 — no framework, authority or country
 * vocabulary appears here):
 *
 * - `draft` — being written internally. It can go out for negotiation, straight
 *   to signature when there is nothing to negotiate, or be abandoned.
 * - `under_negotiation` — with the counterparty. It can come back to `draft`,
 *   go forward to signature, or be abandoned.
 * - `pending_signature` — final text, awaiting execution. It can fall back to
 *   negotiation if the text reopens, come into force, or be abandoned. Coming
 *   into force additionally requires a recorded signature (AC4).
 * - `active` — in force. It leaves force by lapsing (`expired`), by being ended
 *   early (`terminated`) or by being replaced (`superseded`). It never returns
 *   to a drafting state: a change to an in-force contract is a new version or a
 *   new contract, which is what `version_history` and `superseded` are for.
 * - `expired` — lapsed. It can be reopened for renewal (`under_negotiation`) or
 *   marked as replaced (`superseded`). It is NOT re-terminable: terminating a
 *   contract that is no longer in force records a decision that has no effect.
 * - `terminated` / `superseded` — terminal. A contract that was ended or
 *   replaced stays ended or replaced; the successor is a separate record.
 *
 * `deleteContract` still refuses anything but `draft`, so `terminated` is the
 * abandonment path for a contract that has left `draft`.
 */
export const CONTRACT_STATUS_TRANSITIONS = {
    draft: ['under_negotiation', 'pending_signature', 'terminated'],
    under_negotiation: ['draft', 'pending_signature', 'terminated'],
    pending_signature: ['under_negotiation', 'active', 'terminated'],
    active: ['expired', 'terminated', 'superseded'],
    expired: ['under_negotiation', 'superseded'],
    terminated: [],
    superseded: [],
};
/**
 * The statuses a contract may be CREATED at.
 *
 * Sprint 65.5.0 AC4.3 — `active` was reachable at creation, which would have
 * made the signature requirement on the transition into `active` a formality a
 * caller could step around by never entering `pending_signature`. A contract is
 * created at a drafting status and reaches force through the table above.
 */
export const CONTRACT_INITIAL_STATUSES = [
    'draft',
    'under_negotiation',
    'pending_signature',
];
/**
 * The statuses in which a contract is in force.
 *
 * Read by the signature requirement so that a later edit to the table cannot
 * open a second way into force without also passing the signature gate.
 */
export const CONTRACT_STATUSES_IN_FORCE = ['active'];
/**
 * The statuses in which a signature may be recorded — Sprint 65.5.0, lifted
 * into the shared contract by fix-sprint-20260905-2135 F29.
 *
 * `pending_signature` is the ordinary case. `active` is allowed so that a
 * counter-signature or a second signatory can be recorded after the contract
 * came into force; it is not a way in, because the transition into force is
 * itself gated on a signature already being there.
 *
 * **Declared here because two ends read it.** The server owned this list, and
 * the page rendered its record-signature action on the literal
 * `c.status === 'pending_signature'`. So the second half — the whole point of
 * allowing `active` — had no entry point in the product: on an active contract
 * the button was never drawn, and the capability the server's docblock
 * describes was unreachable. That is the same hardcoded-status-list defect the
 * same PR fixed one block below, where a `recordLocked` list named a status
 * that does not exist.
 */
export const CONTRACT_STATUSES_SIGNATURE_RECORDABLE = [
    'pending_signature',
    ...CONTRACT_STATUSES_IN_FORCE,
];
/** Does this transition take the contract out of force? Then it needs a reason. */
export function contractTransitionRemovesFromForce(from, to) {
    return CONTRACT_STATUSES_IN_FORCE.includes(from) && !CONTRACT_STATUSES_IN_FORCE.includes(to);
}
export function isContractTransitionAllowed(from, to) {
    return CONTRACT_STATUS_TRANSITIONS[from].includes(to);
}
// ── Vendor Review Records ──────────────────────────────────────
export const REVIEW_OUTCOMES = [
    'satisfactory',
    'concerns_identified',
    'remediation_required',
    'escalated',
];
/* ── Vendor Onboarding Lifecycle (Sprint 28.3.1) ─────────── */
/**
 * fix-sprint-20261004-63-8-2 F4 — the onboarding case types the case page offers (each has an EN/VI
 * label, `vendor_risk.onboarding.type_*`) and the create route now accepts. The
 * union used to name `new_onboarding` / `material_change` / `periodic_review`,
 * which no screen offered and nothing read; the service defaulted an absent
 * type to `new_onboarding`. A case stored under one of those spellings is shown
 * as stored.
 */
export const VENDOR_ONBOARDING_CASE_TYPES = [
    'new_vendor',
    'service_change',
    'renewal',
    'reassessment',
];
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const VENDOR_ONBOARDING_APPROVAL_GATE_TYPES = ['security', 'legal', 'dpo', 'final'];
/**
 * Sprint 63.30.0 (#592, Q4) — a gate decision that blocks every later gate and
 * the case's move to `approved`. A deferred gate is decided again; a rejected one
 * is reopened only by a documented resolution.
 */
export const VENDOR_ONBOARDING_BLOCKING_DECISIONS = [
    'rejected',
    'deferred',
];
/**
 * Sprint 63.30.0 (#592, decision E5) — the ONE map of which linked record each
 * tab requires, read by `OnboardingCaseService.markTabComplete` (which counts
 * the records on the server and refuses 422 `TAB_LINKED_RECORDS_MISSING`) and
 * by the onboarding page (which names the missing kind). `null`: the tab
 * declares no linked record. A questionnaire, a risk score and a contract are
 * counted for the case's vendor; a sub-processor entry is counted for the case
 * itself (`onboarding_case_id`).
 */
export const VENDOR_ONBOARDING_TAB_REQUIRED_RECORD = {
    overview: null,
    due_diligence: 'questionnaire',
    security_assessment: 'risk_score',
    contractual_controls: 'contract',
    sub_processors: 'sub_processor',
    approval_gates: null,
    evidence: null,
};
/* ── Sprint 41.6.0 V5 — Material-Change Auto-Trigger ──────── */
/**
 * Fields on a VendorOnboardingCase whose change constitutes a "material
 * change" per UC-VN-14 Step 8 and triggers an automatic reassessment.
 * The VendorOnboardingCaseService compares before/after values on these
 * fields during every update; a diff on any one triggers the
 * ReassessmentService.
 */
export const MATERIAL_CHANGE_FIELDS = [
    'data_categories',
    'systems_accessed',
    'jurisdiction',
    'hosting_model',
    'controller_processor_role',
];
//# sourceMappingURL=vendor-risk.js.map